import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { crmAPI } from '../../services/api';
import CRMLayout from './CRMLayout';
import { SUBREGION_TO_PARENT_KEY, OBLAST_TO_KEY } from '../../utils/locationUtils';

const PARENT_REGION_LABELS = {
  tashkent_city: 'Тошкент шаҳри',
  tashkent:      'Тошкент вилояти',
  andijan:       'Андижон вилояти',
  fergana:       'Фарғона вилояти',
  namangan:      'Наманган вилояти',
  samarkand:     'Самарқанд вилояти',
  bukhara:       'Бухоро вилояти',
  navoi:         'Навоий вилояти',
  kashkadarya:   'Қашқадарё вилояти',
  surkhandarya:  'Сурхондарё вилояти',
  jizzakh:       'Жиззах вилояти',
  sirdarya:      'Сирдарё вилояти',
  khorezm:       'Хоразм вилояти',
  karakalpakstan:'Қорақалпоғистон',
};

function getParentKey(location) {
  if (!location) return null;
  return SUBREGION_TO_PARENT_KEY[location] || OBLAST_TO_KEY[location] || null;
}

const TABS = [
  { key: 'notifications', label: 'Хабарномалар' },
  { key: 'stats',         label: 'Статистика' },
  { key: 'ga',            label: 'Трафик кўрсатгичлар' },
  { key: 'staff',         label: 'Ходим фаолияти' },
  { key: 'financials',    label: 'Молия' },
  { key: 'clients',       label: 'Мижозлар' },
  { key: 'attorneys',     label: 'Адвокатлар' },
];

const PRACTICE_AREA_LABELS = {
  divorce: 'Никоҳдан ажратиш', alimony: 'Алиментлар', inheritance: 'Мерос',
  debt_collection: 'Қарз ундириш', fraud: 'Фирибгарлик', ban_check: 'Тақиқни текшириш',
  tax: 'Солиқ', business: 'Тадбиркорлик', credit: 'Кредит', labor: 'Меҳнат низолари',
  traffic_accident: 'Йўл-транспорт ҳодисаси', traffic_fine: 'ГАИ/Жарима',
  housing_compensation: 'Компенсация (уй бузиш)', cadastre: 'Кадастр', hoa: 'УЙ-ЖЭК',
  construction: 'Қурилиш', subsidy: 'Субсидия', corporate: 'Корпоратив',
  criminal: 'Жиноий иш', administrative: 'Маъмурий иш', property_division: 'Мол-мулк бўлиниши',
  intellectual_property: 'Интеллектуал мулк', consumer_rights: 'Истеъмолчи ҳуқуқлари', other: 'Бошқа',
};

const PAGE_SIZE = 20;

const CRMDashboard = () => {
  const [tab, setTab] = useState('notifications');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState({});

  const fetch = async (key) => {
    setLoading(p => ({ ...p, [key]: true }));
    try {
      let res;
      if (key === 'notifications')  res = await crmAPI.getNotifications();
      else if (key === 'stats')      res = await Promise.all([crmAPI.getSurveyStats(), crmAPI.getClickStats(), crmAPI.getSurveyNoReasons(), crmAPI.getCallStats(), crmAPI.getActiveAttorneyStats(), crmAPI.getNoRegionBreakdown()]);
      else if (key === 'ga')         res = await crmAPI.getGoogleAnalyticsStats();
      else if (key === 'staff')      res = await crmAPI.getStaffStats();
      else if (key === 'financials') res = await crmAPI.getFinancials();
      else if (key === 'article-views') res = { data: true }; // self-fetching component
      else if (key === 'clients')    res = await crmAPI.getClients();
      else if (key === 'attorneys')  res = await crmAPI.getAttorneys();
      setData(p => ({ ...p, [key]: Array.isArray(res) ? res.map(r => r.data) : res.data }));
    } catch {
      setData(p => ({ ...p, [key]: [] }));
    } finally {
      setLoading(p => ({ ...p, [key]: false }));
    }
  };

  const load = (key) => { if (!data[key]) fetch(key); };
  const reload = (key) => { setData(p => { const n = { ...p }; delete n[key]; return n; }); fetch(key); };

  useEffect(() => { load(tab); }, [tab]);

  const isLoading = loading[tab];
  const d = data[tab];

  return (
    <CRMLayout>
      <div style={{ padding: '2rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: '800', color: '#1a365d', marginBottom: '1.5rem' }}>Бошқарув панели</h1>

        <div style={{ display: 'flex', gap: '0.5rem', borderBottom: '2px solid #e2e8f0', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: '0.5rem 1.1rem', border: 'none', cursor: 'pointer', fontSize: '0.88rem',
              fontWeight: tab === t.key ? '700' : '400',
              color: tab === t.key ? '#1a365d' : '#718096',
              backgroundColor: 'transparent',
              borderBottom: tab === t.key ? '2px solid #1a365d' : '2px solid transparent',
              marginBottom: '-2px',
            }}>
              {t.label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div style={{ color: '#718096', textAlign: 'center', padding: '3rem' }}>Юкланмоқда...</div>
        ) : (
          <>
            {tab === 'notifications' && <Notifications rows={d || []} onClear={async () => { await crmAPI.clearNotifications(); reload('notifications'); }} />}
            {tab === 'stats'         && <Stats survey={d?.[0] || []} clicks={d?.[1] || {}} noReasons={d?.[2] || []} calls={d?.[3] || {}} activeAttorneys={d?.[4] || {}} noRegionBreakdown={d?.[5] || []} />}
            {tab === 'ga'            && <GoogleAnalytics data={d || {}} />}
            {tab === 'staff'         && <StaffStats rows={d || []} />}
            {tab === 'financials'    && <Financials rows={d || []} />}
            {tab === 'clients'       && <ClientsTable rows={d || []} />}
            {tab === 'attorneys'     && <AttorneysTable rows={d || []} />}
          </>
        )}
      </div>
    </CRMLayout>
  );
};

// ─── Notifications ────────────────────────────────────────────────────────────
const Notifications = ({ rows, onClear }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
    {rows.length > 0 && (
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.25rem' }}>
        <button onClick={onClear} style={{ ...clearBtn, fontSize: '0.82rem' }}>Барчасини ўчириш</button>
      </div>
    )}
    {rows.length === 0 && <Empty />}
    {rows.map((r, i) => (
      <div key={i} style={{ backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e2e8f0', padding: '1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <span style={{ fontSize: '0.75rem', fontWeight: '600', padding: '0.2rem 0.65rem', borderRadius: '20px', marginRight: '0.6rem', backgroundColor: r.type === 'advice' ? '#ebf8ff' : r.type === 'article' ? '#e9d8fd' : '#fef3c7', color: r.type === 'advice' ? '#2b6cb0' : r.type === 'article' ? '#6b46c1' : '#92400e' }}>
            {r.type === 'advice' ? 'Маслаҳат' : r.type === 'article' ? 'Мақола' : 'Обуна'}
          </span>
          <span style={{ fontWeight: '600', color: '#2d3748' }}>{r.payer_name}</span>
          {r.payer_phone && <span style={{ color: '#718096', fontSize: '0.85rem', marginLeft: '0.5rem' }}>{r.payer_phone}</span>}
          <div style={{ fontSize: '0.82rem', color: '#718096', marginTop: '0.2rem' }}>{r.payer_email}</div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          {r.type === 'article' ? (
            <Link to="/crm/articles" style={{ fontWeight: '700', color: '#6b46c1', textDecoration: 'none' }}>
              Кўриб чиқиш →
            </Link>
          ) : (
            <div style={{ fontWeight: '700', color: '#1a365d' }}>{Number(r.amount).toLocaleString()} сўм</div>
          )}
          <div style={{ fontSize: '0.78rem', color: '#a0aec0' }}>{new Date(r.created_at).toLocaleString('uz-UZ')}</div>
        </div>
      </div>
    ))}
  </div>
);

const NO_REASON_LABELS = {
  no_specialization:  'Керакли мутахассислик топилмади',
  no_region:          'Ҳудудимда адвокат топа олмадим',
  no_contact:         'Адвокат билан боғлана олмадим',
  no_info:            'Мутахассислар ҳақида маълумот кам',
  specific_not_found: 'Маълум бир адвокатни қидирдим, лекин топа олмадим',
  other:              'Бошқа',
};

// ─── Stats ────────────────────────────────────────────────────────────────────
const Stats = ({ survey, clicks, noReasons, calls, activeAttorneys, noRegionBreakdown }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
    <div style={card}>
      <h2 style={cardTitle}>Адвокатлар фаоллиги (актив бўлганлар)</h2>
      <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
        <Stat label="Жами адвокатлар" value={activeAttorneys.total || 0} />
        <Stat label="Сўнгги 24 соат" value={activeAttorneys.active24h || 0} />
        <Stat label="Сўнгги 7 кун" value={activeAttorneys.active7d || 0} />
        <Stat label="Сўнгги 30 кун" value={activeAttorneys.active30d || 0} />
        <Stat label="Тўловли обуна" value={activeAttorneys.paidSubscribed || 0} />
      </div>
    </div>

    <div style={card}>
      <h2 style={cardTitle}>Сўровнома — Адвокат топа олдингизми?</h2>
      {(!survey || survey.length === 0) ? <Empty /> : survey.map((row, i) => {
        const total = parseInt(row.total) || 1;
        const yesPct = Math.round((parseInt(row.yes_count) / total) * 100);
        return (
          <div key={i} style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.9rem', color: '#4a5568', marginBottom: '0.5rem' }}>{row.question}</div>
            <div style={{ display: 'flex', gap: '1rem', fontSize: '0.85rem', marginBottom: '0.4rem' }}>
              <span style={{ color: '#276749' }}>✓ Ҳа: {row.yes_count} ({yesPct}%)</span>
              <span style={{ color: '#c53030' }}>✗ Йўқ: {row.no_count} ({100 - yesPct}%)</span>
              <span style={{ color: '#718096' }}>Жами: {row.total}</span>
            </div>
            <div style={{ height: '8px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ width: `${yesPct}%`, height: '100%', backgroundColor: '#38a169' }} />
            </div>
          </div>
        );
      })}
    </div>

    <div style={card}>
      <h2 style={cardTitle}>Сўровнома — Адвокат топа олмаганларнинг сабаблари</h2>
      {(!noReasons || noReasons.length === 0) ? <Empty /> : (
        <BarChart rows={noReasons.map(r => ({ label: NO_REASON_LABELS[r.reason] || r.reason, count: r.count }))} color="#e53e3e" />
      )}
    </div>

    <div style={card}>
      <h2 style={cardTitle}>«Ҳудудимда адвокат топа олмадим» — ҳудудлар бўйича</h2>
      {(!noRegionBreakdown || noRegionBreakdown.length === 0) ? <Empty /> : (
        <BarChart rows={noRegionBreakdown.map(r => ({ label: r.region, count: r.count }))} color="#dd6b20" />
      )}
    </div>

    <div style={card}>
      <h2 style={cardTitle}>Адвокат топиш — қидирув статистикаси</h2>
      <div style={{ display: 'flex', gap: '2rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <Stat label="Жами" value={clicks.total || 0} />
        <Stat label="Охирги 30 кун" value={clicks.last30Days || 0} />
      </div>
      {clicks.byArea?.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#4a5568', marginBottom: '0.6rem' }}>Мутахассислик бўйича</div>
          <BarChart rows={clicks.byArea.map(r => ({ label: PRACTICE_AREA_LABELS[r.practice_area] || r.practice_area || 'Мутахассислик танланмаган', count: r.count }))} />
        </div>
      )}
      {clicks.byLocation?.length > 0 && (
        <div style={{ marginBottom: clicks.byName?.length > 0 ? '1.5rem' : 0 }}>
          <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#4a5568', marginBottom: '0.6rem' }}>Ҳудуд бўйича</div>
          <BarChart rows={clicks.byLocation.map(r => ({ label: r.location, count: r.count }))} color="#805ad5" />
        </div>
      )}
      {clicks.byName?.length > 0 && (
        <div>
          <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#4a5568', marginBottom: '0.6rem' }}>Исм бўйича қидирилганлар</div>
          <BarChart rows={clicks.byName.map(r => ({ label: r.term, count: r.count }))} color="#d69e2e" />
        </div>
      )}
    </div>

    <div style={card}>
      <h2 style={cardTitle}>Адвокат телефонига қўнғироқлар</h2>
      <div style={{ display: 'flex', gap: '2rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <Stat label="Жами" value={calls.total || 0} />
        <Stat label="Охирги 30 кун" value={calls.last30Days || 0} />
      </div>
      {calls.byAttorney?.length > 0 && (
        <div>
          <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#4a5568', marginBottom: '0.6rem' }}>Адвокат бўйича</div>
          <BarChart rows={calls.byAttorney.map(r => ({ label: r.full_name, count: r.count }))} color="#38a169" />
        </div>
      )}
    </div>
  </div>
);

// ─── Google Analytics ─────────────────────────────────────────────────────────
const CHANNEL_LABELS = {
  Direct: 'Тўғридан-тўғри', 'Organic Search': 'Қидирув тизимлари', Referral: 'Ҳавола орқали',
  'Organic Social': 'Ижтимоий тармоқлар', 'Paid Search': 'Реклама (қидирув)',
  'Paid Social': 'Реклама (ижтимоий)', Email: 'Email', Unassigned: 'Номаълум', 'Cross-network': 'Аралаш',
};
const DEVICE_LABELS = { mobile: 'Мобиль', desktop: 'Компьютер', tablet: 'Планшет' };
const PAGE_LABELS = { '/': 'Бош саҳифа' };
const pageLabel = (path) => PAGE_LABELS[path] || path;

const formatDuration = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const OVERVIEW_ROWS = [
  { key: 'activeUsers', label: 'Фойдаланувчилар', fmt: (v) => v },
  { key: 'sessions', label: 'Ташрифлар', fmt: (v) => v },
  { key: 'pageViews', label: 'Саҳифа кўришлар', fmt: (v) => v },
  { key: 'avgSessionSeconds', label: 'Ўртача давомийлик', fmt: formatDuration },
  {
    key: 'bounceRate', label: 'Нофаол ташрифлар', fmt: (v) => `${Math.round(v * 100)}%`,
    info: 'Фойдаланувчи фақат битта саҳифани кўриб, сайтда бошқа ҳеч қандай ҳаракат қилмасдан (босмасдан) тарк этган ташрифлар улуши',
  },
];

const OverviewTable = ({ last24, last7, last30 }) => (
  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
    <thead>
      <tr style={{ backgroundColor: '#f7fafc' }}>
        <th style={{ padding: '0.6rem 1rem', textAlign: 'left', fontSize: '0.78rem', fontWeight: '600', color: '#718096', textTransform: 'uppercase' }}>Кўрсаткич</th>
        <th style={{ padding: '0.6rem 1rem', textAlign: 'right', fontSize: '0.78rem', fontWeight: '600', color: '#718096', textTransform: 'uppercase' }}>Охирги 24 соат</th>
        <th style={{ padding: '0.6rem 1rem', textAlign: 'right', fontSize: '0.78rem', fontWeight: '600', color: '#718096', textTransform: 'uppercase' }}>Охирги 7 кун</th>
        <th style={{ padding: '0.6rem 1rem', textAlign: 'right', fontSize: '0.78rem', fontWeight: '600', color: '#718096', textTransform: 'uppercase' }}>Охирги 30 кун</th>
      </tr>
    </thead>
    <tbody>
      {OVERVIEW_ROWS.map(row => (
        <tr key={row.key} style={{ borderTop: '1px solid #f0f4f8' }}>
          <td style={{ ...td, color: '#4a5568' }}>
            {row.info ? <InfoLabel text={row.label} info={row.info} /> : row.label}
          </td>
          <td style={{ ...td, textAlign: 'right', fontWeight: '700', color: '#1a365d' }}>{last24 ? row.fmt(last24[row.key]) : '—'}</td>
          <td style={{ ...td, textAlign: 'right', fontWeight: '700', color: '#1a365d' }}>{last7 ? row.fmt(last7[row.key]) : '—'}</td>
          <td style={{ ...td, textAlign: 'right', fontWeight: '700', color: '#1a365d' }}>{last30 ? row.fmt(last30[row.key]) : '—'}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const InfoLabel = ({ text, info }) => {
  const [hover, setHover] = useState(false);
  return (
    <span
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', cursor: 'default' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {text}
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '14px', height: '14px',
        borderRadius: '50%', border: '1px solid #a0aec0', color: '#a0aec0', fontSize: '10px', fontWeight: '700', lineHeight: 1,
      }}>i</span>
      {hover && (
        <span style={{
          position: 'absolute', left: 0, top: '135%', zIndex: 20, width: '260px',
          backgroundColor: '#1a365d', color: 'white', borderRadius: '8px', padding: '0.6rem 0.75rem',
          fontSize: '0.78rem', fontWeight: '400', lineHeight: 1.5, boxShadow: '0 4px 14px rgba(0,0,0,0.22)',
        }}>
          {info}
        </span>
      )}
    </span>
  );
};

const GoogleAnalytics = ({ data }) => {
  const { dailyTrend = [], summary = {}, topPages = [], trafficSources = [], devices = [] } = data;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={card}>
        <h2 style={cardTitle}>Сайт трафиги — умумий кўрсаткичлар</h2>
        <OverviewTable last24={summary.last24Hours} last7={summary.last7Days} last30={summary.last30Days} />
      </div>

      <div style={card}>
        <h2 style={cardTitle}>Кунлик фойдаланувчилар (30 кун)</h2>
        {dailyTrend.length === 0 ? <Empty /> : <Sparkline rows={dailyTrend} />}
      </div>

      <div style={card}>
        <h2 style={cardTitle}>Энг кўп кўрилган саҳифалар (30 кун)</h2>
        {topPages.length === 0 ? <Empty /> : (
          <BarChart rows={topPages.map(r => ({ label: pageLabel(r.path), count: r.pageViews }))} color="#3182ce" />
        )}
      </div>

      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
        <div style={{ ...card, flex: '1 1 300px' }}>
          <h2 style={cardTitle}>Трафик манбалари (30 кун)</h2>
          {trafficSources.length === 0 ? <Empty /> : (
            <BarChart rows={trafficSources.map(r => ({ label: CHANNEL_LABELS[r.channel] || r.channel, count: r.sessions }))} color="#805ad5" />
          )}
        </div>
        <div style={{ ...card, flex: '1 1 300px' }}>
          <h2 style={cardTitle}>Қурилмалар (30 кун)</h2>
          {devices.length === 0 ? <Empty /> : (
            <BarChart rows={devices.map(r => ({ label: DEVICE_LABELS[r.device] || r.device, count: r.activeUsers }))} color="#38a169" />
          )}
        </div>
      </div>
    </div>
  );
};

const fmtDateShort = (d) => `${d.slice(6, 8)}.${d.slice(4, 6)}`;
const fmtDateFull = (d) => `${d.slice(6, 8)}.${d.slice(4, 6)}.${d.slice(0, 4)}`;

const Sparkline = ({ rows }) => {
  const width = 760, height = 180, pad = 24, topPad = 16;
  const [hoverIdx, setHoverIdx] = useState(null);
  const svgRef = useRef(null);

  const max = Math.max(...rows.map(r => r.activeUsers), 1);
  const stepX = (width - pad * 2) / Math.max(rows.length - 1, 1);
  const points = rows.map((r, i) => ({
    x: pad + i * stepX,
    y: height - pad - (r.activeUsers / max) * (height - pad - topPad),
  }));

  const handleMove = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const relX = ((e.clientX - rect.left) / rect.width) * width;
    let idx = Math.round((relX - pad) / stepX);
    idx = Math.max(0, Math.min(rows.length - 1, idx));
    setHoverIdx(idx);
  };

  const tooltipLeft = hoverIdx == null ? 0 : Math.min(Math.max(points[hoverIdx].x - 75, 0), width - 155);

  return (
    <div style={{ position: 'relative', overflowX: 'auto' }}>
      <svg
        ref={svgRef}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        style={{ display: 'block', minWidth: width, cursor: 'crosshair' }}
        onMouseMove={handleMove}
        onMouseLeave={() => setHoverIdx(null)}
      >
        <polyline points={points.map(p => `${p.x},${p.y}`).join(' ')} fill="none" stroke="#3182ce" strokeWidth="2" />
        {hoverIdx != null && (
          <line x1={points[hoverIdx].x} y1={topPad} x2={points[hoverIdx].x} y2={height - pad} stroke="#cbd5e0" strokeWidth="1" strokeDasharray="3,3" />
        )}
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={hoverIdx === i ? 5 : 2.5} fill={hoverIdx === i ? '#1a365d' : '#3182ce'} />
        ))}
        {rows.map((r, i) => (
          i % 4 === 0 && (
            <text key={i} x={points[i].x} y={height - 4} fontSize="10" fill="#a0aec0" textAnchor="middle">
              {fmtDateShort(r.date)}
            </text>
          )
        ))}
      </svg>
      {hoverIdx != null && (
        <div style={{
          position: 'absolute', left: tooltipLeft, top: Math.max(points[hoverIdx].y - 92, 0),
          backgroundColor: '#1a365d', color: 'white', borderRadius: '8px', padding: '0.6rem 0.8rem',
          fontSize: '0.78rem', lineHeight: 1.5, pointerEvents: 'none', boxShadow: '0 4px 14px rgba(0,0,0,0.22)',
          zIndex: 10, minWidth: '145px',
        }}>
          <div style={{ fontWeight: '700', marginBottom: '0.25rem' }}>{fmtDateFull(rows[hoverIdx].date)}</div>
          <div>Фойдаланувчилар: {rows[hoverIdx].activeUsers}</div>
          <div>Ташрифлар: {rows[hoverIdx].sessions}</div>
          <div>Кўришлар: {rows[hoverIdx].pageViews}</div>
        </div>
      )}
    </div>
  );
};

const BarChart = ({ rows, color = '#3182ce' }) => {
  const max = Math.max(...rows.map(r => parseInt(r.count)), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
      {rows.slice(0, 15).map((r, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ width: '180px', fontSize: '0.8rem', color: '#4a5568', textAlign: 'right', flexShrink: 0 }}>{r.label}</div>
          <div style={{ flex: 1, height: '16px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ width: `${Math.round((parseInt(r.count) / max) * 100)}%`, height: '100%', backgroundColor: color }} />
          </div>
          <div style={{ width: '30px', fontSize: '0.8rem', color: '#718096' }}>{r.count}</div>
        </div>
      ))}
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div style={{ textAlign: 'center' }}>
    <div style={{ fontSize: '1.6rem', fontWeight: '800', color: '#1a365d' }}>{value}</div>
    <div style={{ fontSize: '0.8rem', color: '#718096' }}>{label}</div>
  </div>
);

// ─── Staff Stats ──────────────────────────────────────────────────────────────
const StaffStats = ({ rows }) => (
  <TableWrapper cols={['Ходим', 'Олган', 'Жавоб берган', 'Ўрт. вақт (дақ.)']}>
    {rows.length === 0 ? <EmptyRow cols={4} /> : rows.map(r => (
      <tr key={r.id} style={{ borderTop: '1px solid #f0f4f8' }}>
        <td style={td}>{r.name}</td>
        <td style={td}>{r.taken}</td>
        <td style={td}>{r.answered}</td>
        <td style={td}>{r.avg_minutes ?? '—'}</td>
      </tr>
    ))}
  </TableWrapper>
);

// ─── Financials with filters + pagination ─────────────────────────────────────
const Financials = ({ rows }) => {
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]   = useState('');
  const [page, setPage]       = useState(1);

  const filtered = useMemo(() => {
    let r = rows;
    if (typeFilter) r = r.filter(x => x.type === typeFilter);
    if (dateFrom) r = r.filter(x => new Date(x.created_at) >= new Date(dateFrom));
    if (dateTo)   r = r.filter(x => new Date(x.created_at) <= new Date(dateTo + 'T23:59:59'));
    return r;
  }, [rows, typeFilter, dateFrom, dateTo]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const total = filtered.reduce((s, r) => s + parseFloat(r.amount || 0), 0);

  const resetPage = () => setPage(1);

  return (
    <div>
      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <FilterSelect
          label="Тури"
          value={typeFilter}
          onChange={(v) => { setTypeFilter(v); resetPage(); }}
          options={[{ value: 'advice', label: 'Маслаҳат' }, { value: 'subscription', label: 'Обуна' }]}
        />
        <FilterDate label="Дан" value={dateFrom} onChange={e => { setDateFrom(e.target.value); resetPage(); }} />
        <FilterDate label="Гача" value={dateTo} onChange={e => { setDateTo(e.target.value); resetPage(); }} />
        {(typeFilter || dateFrom || dateTo) && (
          <button onClick={() => { setTypeFilter(''); setDateFrom(''); setDateTo(''); resetPage(); }} style={{ alignSelf: 'flex-end', ...clearBtn }}>
            Тозалаш
          </button>
        )}
      </div>

      <TableWrapper cols={['Сана', 'Тури', 'Сумма', 'Тўловчи', 'Телефон', 'Email']}>
        {paged.length === 0 ? <EmptyRow cols={6} /> : paged.map((r, i) => (
          <tr key={i} style={{ borderTop: '1px solid #f0f4f8' }}>
            <td style={td}>{new Date(r.created_at).toLocaleDateString('uz-UZ')}</td>
            <td style={td}>
              <span style={{ backgroundColor: r.type === 'advice' ? '#ebf8ff' : '#fef3c7', color: r.type === 'advice' ? '#2b6cb0' : '#92400e', padding: '0.15rem 0.5rem', borderRadius: '12px', fontSize: '0.78rem', fontWeight: '600' }}>
                {r.type === 'advice' ? 'Маслаҳат' : 'Обуна'}
              </span>
            </td>
            <td style={{ ...td, fontWeight: '600' }}>{Number(r.amount).toLocaleString()} сўм</td>
            <td style={td}>{r.payer_name}</td>
            <td style={td}>{r.payer_phone}</td>
            <td style={td}>{r.payer_email}</td>
          </tr>
        ))}
        {filtered.length > 0 && (
          <tr style={{ backgroundColor: '#f7fafc', borderTop: '2px solid #e2e8f0' }}>
            <td style={{ ...td, fontWeight: '700' }} colSpan={2}>Жами ({filtered.length} та)</td>
            <td style={{ ...td, fontWeight: '800', color: '#1a365d' }}>{total.toLocaleString()} сўм</td>
            <td colSpan={3} />
          </tr>
        )}
      </TableWrapper>

      <Pagination page={page} totalPages={totalPages} onPage={setPage} total={filtered.length} />
    </div>
  );
};

// ─── Clients with filters ─────────────────────────────────────────────────────
const ClientsTable = ({ rows }) => {
  const locations = useMemo(() => [...new Set(rows.map(r => r.location).filter(Boolean))].sort(), [rows]);
  const [locFilter, setLocFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]   = useState('');
  const [page, setPage]       = useState(1);

  const filtered = useMemo(() => {
    let r = rows;
    if (locFilter) r = r.filter(x => x.location === locFilter);
    if (dateFrom)  r = r.filter(x => new Date(x.created_at) >= new Date(dateFrom));
    if (dateTo)    r = r.filter(x => new Date(x.created_at) <= new Date(dateTo + 'T23:59:59'));
    return r;
  }, [rows, locFilter, dateFrom, dateTo]);

  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const resetPage = () => setPage(1);

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <FilterSelect
          label="Ҳудуд"
          value={locFilter}
          onChange={(v) => { setLocFilter(v); resetPage(); }}
          options={locations.map(l => ({ value: l, label: l }))}
        />
        <FilterDate label="Дан" value={dateFrom} onChange={e => { setDateFrom(e.target.value); resetPage(); }} />
        <FilterDate label="Гача" value={dateTo} onChange={e => { setDateTo(e.target.value); resetPage(); }} />
        {(locFilter || dateFrom || dateTo) && (
          <button onClick={() => { setLocFilter(''); setDateFrom(''); setDateTo(''); resetPage(); }} style={{ alignSelf: 'flex-end', ...clearBtn }}>
            Тозалаш
          </button>
        )}
      </div>

      <TableWrapper cols={['Исм', 'Телефон', 'Жой', 'Email', 'Рўйхатдан ўтган']}>
        {paged.length === 0 ? <EmptyRow cols={5} /> : paged.map(r => (
          <tr key={r.id} style={{ borderTop: '1px solid #f0f4f8' }}>
            <td style={td}>{r.full_name}</td>
            <td style={td}>{r.phone}</td>
            <td style={td}>{r.location}</td>
            <td style={td}>{r.email}</td>
            <td style={td}>{new Date(r.created_at).toLocaleDateString('uz-UZ')}</td>
          </tr>
        ))}
      </TableWrapper>
      <Pagination page={page} totalPages={totalPages} onPage={setPage} total={filtered.length} />
    </div>
  );
};

// ─── Attorneys with filters ───────────────────────────────────────────────────
const AttorneysTable = ({ rows }) => {
  const parentRegions = useMemo(() => {
    const keys = [...new Set(rows.map(r => getParentKey(r.location)).filter(Boolean))];
    return keys
      .filter(k => PARENT_REGION_LABELS[k])
      .sort((a, b) => PARENT_REGION_LABELS[a].localeCompare(PARENT_REGION_LABELS[b]));
  }, [rows]);

  const [locFilters, setLocFilters] = useState([]); // checkbox multi-select of parent-region keys
  const [subFilter, setSubFilter]  = useState('');
  const [dateFrom, setDateFrom]    = useState('');
  const [dateTo, setDateTo]        = useState('');
  const [page, setPage]            = useState(1);
  // Ordered list of active sort columns — [0] is the primary key, later entries are
  // tiebreakers, most-recently-clicked column always becomes the new primary. So
  // click "Сўнгги кириш" then "Обуна" and you get paid attorneys grouped first, each
  // group still ordered by most-recently-seen (no modifier key needed — every click
  // stacks on top of what's already there; clicking the current primary again just
  // flips its direction).
  const [sort, setSort] = useState([{ key: 'last_login', dir: 'desc' }]);
  const toggleSort = (key, defaultDir) => setSort(cur => {
    if (cur[0]?.key === key) {
      const copy = [...cur];
      copy[0] = { key, dir: copy[0].dir === 'asc' ? 'desc' : 'asc' };
      return copy;
    }
    const prior = cur.find(s => s.key === key);
    const rest = cur.filter(s => s.key !== key);
    return [{ key, dir: prior ? prior.dir : defaultDir }, ...rest].slice(0, 3);
  });

  const SORT_VALUE = {
    last_login:    r => r.last_login ? new Date(r.last_login).getTime() : null,
    location:      r => r.location || null,
    is_subscribed: r => r.is_subscribed ? 1 : 0,
  };
  const compareBy = (key, dir, a, b) => {
    const av = SORT_VALUE[key](a), bv = SORT_VALUE[key](b);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;  // missing values always sink to the bottom, regardless of direction
    if (bv == null) return -1;
    const mul = dir === 'desc' ? -1 : 1;
    return typeof av === 'string' ? mul * av.localeCompare(bv, 'uz') : mul * (av - bv);
  };

  const filtered = useMemo(() => {
    let r = rows;
    if (locFilters.length) r = r.filter(x => locFilters.includes(getParentKey(x.location)));
    if (subFilter === 'active')   r = r.filter(x => x.is_subscribed);
    if (subFilter === 'inactive') r = r.filter(x => !x.is_subscribed);
    if (dateFrom)  r = r.filter(x => new Date(x.created_at) >= new Date(dateFrom));
    if (dateTo)    r = r.filter(x => new Date(x.created_at) <= new Date(dateTo + 'T23:59:59'));
    r = [...r].sort((a, b) => {
      for (const s of sort) {
        const c = compareBy(s.key, s.dir, a, b);
        if (c !== 0) return c;
      }
      return 0;
    });
    return r;
  }, [rows, locFilters, subFilter, dateFrom, dateTo, sort]);

  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const resetPage = () => setPage(1);

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <FilterMultiSelect
          label="Жой"
          value={locFilters}
          onChange={(v) => { setLocFilters(v); resetPage(); }}
          options={parentRegions.map(k => ({ value: k, label: PARENT_REGION_LABELS[k] }))}
        />
        <FilterSelect
          label="Обуна"
          value={subFilter}
          onChange={(v) => { setSubFilter(v); resetPage(); }}
          options={[{ value: 'active', label: 'Фаол' }, { value: 'inactive', label: 'Фаол эмас' }]}
        />
        <FilterDate label="Дан" value={dateFrom} onChange={e => { setDateFrom(e.target.value); resetPage(); }} />
        <FilterDate label="Гача" value={dateTo} onChange={e => { setDateTo(e.target.value); resetPage(); }} />
        {(locFilters.length > 0 || subFilter || dateFrom || dateTo) && (
          <button onClick={() => { setLocFilters([]); setSubFilter(''); setDateFrom(''); setDateTo(''); resetPage(); }} style={{ alignSelf: 'flex-end', ...clearBtn }}>
            Тозалаш
          </button>
        )}
      </div>

      <TableWrapper cols={['Исм', 'Телефон', 'Жой',
        'Лицензия', sortCol(sort, toggleSort, 'is_subscribed', 'Обуна', 'desc'),
        'Email', 'Рўйхатдан ўтган', sortCol(sort, toggleSort, 'last_login', 'Сўнгги кириш', 'desc'),
      ]}>
        {paged.length === 0 ? <EmptyRow cols={8} /> : paged.map(r => (
          <tr key={r.id} style={{ borderTop: '1px solid #f0f4f8' }}>
            <td style={td}>{r.full_name}</td>
            <td style={td}>{r.phone}</td>
            <td style={td}>{r.location}</td>
            <td style={td}>{r.bar_number}</td>
            <td style={td}>
              {r.is_subscribed
                ? <span style={{ color: '#276749', fontWeight: '600' }}>✓ Фаол</span>
                : <span style={{ color: '#718096' }}>Йўқ</span>}
            </td>
            <td style={td}>{r.email}</td>
            <td style={td}>{new Date(r.created_at).toLocaleDateString('uz-UZ')}</td>
            <td style={td}>
              {r.last_login
                ? new Date(r.last_login).toLocaleString('uz-UZ')
                : <span style={{ color: '#a0aec0' }}>—</span>}
            </td>
          </tr>
        ))}
      </TableWrapper>
      <Pagination page={page} totalPages={totalPages} onPage={setPage} total={filtered.length} />
    </div>
  );
};

// ─── Pagination ───────────────────────────────────────────────────────────────
const Pagination = ({ page, totalPages, onPage, total }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
    <div style={{ fontSize: '0.82rem', color: '#718096' }}>Жами: {total} та</div>
    {totalPages > 1 && (
      <div style={{ display: 'flex', gap: '0.3rem' }}>
        <PageBtn onClick={() => onPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹</PageBtn>
        {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
          const n = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= totalPages - 3 ? totalPages - 6 + i : page - 3 + i;
          return <PageBtn key={n} onClick={() => onPage(n)} active={page === n}>{n}</PageBtn>;
        })}
        <PageBtn onClick={() => onPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>›</PageBtn>
      </div>
    )}
  </div>
);

const PageBtn = ({ onClick, disabled, active, children }) => (
  <button onClick={onClick} disabled={disabled} style={{
    minWidth: '32px', height: '32px', padding: '0 0.4rem', border: '1px solid #e2e8f0',
    borderRadius: '6px', cursor: disabled ? 'not-allowed' : 'pointer', fontSize: '0.85rem',
    backgroundColor: active ? '#1a365d' : 'white', color: active ? 'white' : disabled ? '#cbd5e0' : '#4a5568',
    fontWeight: active ? '700' : '400',
  }}>
    {children}
  </button>
);

// ─── Shared ───────────────────────────────────────────────────────────────────
const thStyle = { padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.78rem', fontWeight: '600', color: '#718096', textTransform: 'uppercase', whiteSpace: 'nowrap' };

/** Builds a sortable TableWrapper column entry for a `sort` chain ([{key,dir}, ...]) +
 *  its `toggleSort(key, defaultDir)` setter. Every click promotes that column to primary
 *  sort, pushing whatever was already active down into tiebreaker slots (so clicking
 *  Обуна after Сўнгги кириш groups paid attorneys first, each group still ordered by
 *  most-recently-seen) — clicking the current primary again just flips its direction. */
const sortCol = (sort, toggleSort, key, label, defaultDir) => {
  const idx = sort.findIndex(s => s.key === key);
  return {
    key, label,
    dir: idx === -1 ? null : sort[idx].dir,
    order: sort.length > 1 && idx !== -1 ? idx + 1 : null,
    onClick: () => toggleSort(key, defaultDir),
  };
};

const TableWrapper = ({ cols, children }) => (
  <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
      <thead>
        <tr style={{ backgroundColor: '#f7fafc' }}>
          {cols.map(c => typeof c === 'string'
            ? <th key={c} style={thStyle}>{c}</th>
            : (
              <th key={c.key} style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }} onClick={c.onClick} title="Саралаш учун босинг">
                {c.label}{' '}
                <span style={{ fontSize: '0.7rem', opacity: c.dir ? 1 : 0.35 }}>
                  {c.dir === 'desc' ? '▼' : c.dir === 'asc' ? '▲' : '↕'}
                </span>
                {c.order && <sup style={{ fontSize: '0.62rem', marginLeft: '2px', color: '#4a5568', fontWeight: '700' }}>{c.order}</sup>}
              </th>
            ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

const EmptyRow = ({ cols }) => (
  <tr><td colSpan={cols} style={{ textAlign: 'center', padding: '2rem', color: '#718096' }}>Маълумот йўқ</td></tr>
);

const Empty = () => <div style={{ color: '#718096', padding: '1rem 0' }}>Маълумот йўқ</div>;

const card      = { backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '1.5rem' };
const cardTitle = { fontSize: '1rem', fontWeight: '700', color: '#1a365d', marginBottom: '1rem' };
const td        = { padding: '0.75rem 1rem', fontSize: '0.88rem', color: '#2d3748' };
const filterLabel  = { display: 'block', fontSize: '0.78rem', fontWeight: '600', color: '#4a5568', marginBottom: '0.3rem' };
const clearBtn     = { backgroundColor: '#fed7d7', color: '#c53030', border: 'none', padding: '0.5rem 0.85rem', borderRadius: '8px', fontSize: '0.88rem', cursor: 'pointer', fontWeight: '600', fontFamily: 'inherit' };

// ─── Filter components ────────────────────────────────────────────────────────
const TRIGGER_STYLE = {
  display: 'flex', alignItems: 'center', gap: '0.5rem',
  backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px',
  padding: '0.5rem 0.9rem', cursor: 'pointer', fontFamily: 'inherit',
  fontSize: '0.88rem', color: '#2d3748', fontWeight: '500', minWidth: '140px',
  justifyContent: 'space-between', userSelect: 'none',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
};

const FilterSelect = ({ label, value, onChange, options, placeholder = 'Барчаси' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selectedLabel = options.find(o => o.value === value)?.label || placeholder;

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref}>
      {label && <div style={filterLabel}>{label}</div>}
      <div style={{ position: 'relative' }}>
        <div onClick={() => setOpen(p => !p)} style={{ ...TRIGGER_STYLE, color: value ? '#1a365d' : '#718096' }}>
          <span>{selectedLabel}</span>
          <svg width="10" height="6" viewBox="0 0 10 6" style={{ flexShrink: 0, transition: 'transform 0.15s', transform: open ? 'rotate(180deg)' : 'none' }}>
            <path d="M1 1l4 4 4-4" stroke="#718096" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        {open && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 200,
            backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e2e8f0',
            boxShadow: '0 8px 32px rgba(0,0,0,0.14)', minWidth: '100%', overflow: 'hidden',
          }}>
            {[{ value: '', label: placeholder }, ...options].map(opt => (
              <DropdownOpt
                key={opt.value}
                active={value === opt.value}
                onClick={() => { onChange(opt.value); setOpen(false); }}
              >
                {opt.label}
              </DropdownOpt>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/** Checkbox dropdown — like FilterSelect but lets several options be checked at once
 *  (e.g. multiple regions), ANDed together as an OR-filter (row matches if it's in any
 *  of the checked values). Doesn't close on selection, so you can check several in a row. */
const FilterMultiSelect = ({ label, value, onChange, options, placeholder = 'Барчаси' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selectedLabel = value.length === 0
    ? placeholder
    : value.length === 1
      ? (options.find(o => o.value === value[0])?.label || placeholder)
      : `${value.length} та танланган`;

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (v) => onChange(value.includes(v) ? value.filter(x => x !== v) : [...value, v]);

  return (
    <div ref={ref}>
      {label && <div style={filterLabel}>{label}</div>}
      <div style={{ position: 'relative' }}>
        <div onClick={() => setOpen(p => !p)} style={{ ...TRIGGER_STYLE, color: value.length ? '#1a365d' : '#718096' }}>
          <span>{selectedLabel}</span>
          <svg width="10" height="6" viewBox="0 0 10 6" style={{ flexShrink: 0, transition: 'transform 0.15s', transform: open ? 'rotate(180deg)' : 'none' }}>
            <path d="M1 1l4 4 4-4" stroke="#718096" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        {open && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 200,
            backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e2e8f0',
            boxShadow: '0 8px 32px rgba(0,0,0,0.14)', minWidth: '100%', overflow: 'hidden',
          }}>
            {value.length > 0 && (
              <DropdownOpt onClick={() => onChange([])}>
                <span style={{ color: '#c53030' }}>Тозалаш</span>
              </DropdownOpt>
            )}
            {options.map(opt => (
              <CheckboxOpt key={opt.value} checked={value.includes(opt.value)} onClick={() => toggle(opt.value)}>
                {opt.label}
              </CheckboxOpt>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const CheckboxOpt = ({ onClick, checked, children }) => {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '0.55rem 1rem', fontSize: '0.88rem', cursor: 'pointer', fontFamily: 'inherit',
        backgroundColor: hover ? '#f7fafc' : 'transparent',
        color: checked ? '#1a365d' : '#2d3748', fontWeight: checked ? '600' : '400',
        display: 'flex', alignItems: 'center', gap: '0.6rem',
      }}
    >
      <span style={{
        width: '15px', height: '15px', borderRadius: '4px', flexShrink: 0,
        border: `1.5px solid ${checked ? '#1a365d' : '#cbd5e0'}`,
        backgroundColor: checked ? '#1a365d' : 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {checked && <svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3.5L3 5.5L8 1" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>}
      </span>
      {children}
    </div>
  );
};

const DropdownOpt = ({ onClick, active, children }) => {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '0.55rem 1rem', fontSize: '0.88rem', cursor: 'pointer', fontFamily: 'inherit',
        backgroundColor: hover ? '#f7fafc' : 'transparent',
        color: active ? '#1a365d' : '#2d3748',
        fontWeight: active ? '600' : '400',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem',
      }}
    >
      {children}
      {active && <svg width="12" height="9" viewBox="0 0 12 9" fill="none"><path d="M1 4l3.5 3.5L11 1" stroke="#1a365d" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
    </div>
  );
};

const FilterDate = ({ label, value, onChange }) => {
  const ref = useRef(null);
  const open = () => { try { ref.current?.showPicker(); } catch {} };
  const display = value ? new Date(value + 'T00:00:00').toLocaleDateString('uz-UZ') : null;
  return (
    <div>
      {label && <div style={filterLabel}>{label}</div>}
      <div style={{ position: 'relative', display: 'inline-block' }} onClick={open}>
        {/* Custom display — always visible, never shows native dd/mm/yyyy */}
        <div style={{ ...TRIGGER_STYLE, minWidth: '140px', paddingRight: '2rem', color: value ? '#1a365d' : '#718096', pointerEvents: 'none' }}>
          <span>{display || 'Сана'}</span>
        </div>
        {/* Native input fully transparent, sits on top to capture click → opens picker */}
        <input
          ref={ref}
          type="date"
          value={value}
          onChange={onChange}
          style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%', zIndex: 1 }}
        />
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#718096" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{ position: 'absolute', right: '0.65rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', zIndex: 2 }}>
          <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      </div>
    </div>
  );
};

export default CRMDashboard;
